import React from "react";
import Icon from "../Icon";
import Flex, { FlexProps } from "../Flex";
import Text from "../Text";
import Button, { ButtonProps } from "../Button";
import { BoxProps } from "../Box";

type Props = {
  id?: string;
  button?: ButtonProps;
  secondaryButton?: ButtonProps;
  noticeStyle?: "info" | "warning" | "error" | "neutral";
  // A fragment can be pass to include nested tags like formatting or a tertiary button
  message: string | React.ReactNode;
} & BoxProps &
  FlexProps;

const Notice: React.FC<Props> = ({
  id = null,
  noticeStyle = "info",
  message,
  button = null,
  secondaryButton = null,
  ...rest
}) => {
  const style = {
    border: `1px solid ${noticeStyle}`,
    background: `${noticeStyle}Light`,
  };

  function renderIcon() {
    const iconName = () => {
      switch (noticeStyle) {
        case "warning":
          return "info.warning";
        case "error":
          return "info.error";
        case "info":
        case "neutral":
        default:
          return "info.info";
      }
    };
    return (
      <Icon
        data-testid="notice-icon"
        size={16}
        fill="black"
        iconId={iconName()}
        flexShrink={0}
        mr="s"
      />
    );
  }
  function renderButton() {
    if (!button) return null;

    const theme = {
      primaryColor: noticeStyle,
      primaryColorDark: `${noticeStyle}Dark`,
      primaryColorLight: `${noticeStyle}Light`,
      primaryColorTint: ["warning"].includes(noticeStyle) ? "black" : "white",
    };

    return (
      <Button
        buttonStyle="primary"
        themeOverrides={theme}
        {...button}
        justifySelf="flex-end"
        size="small"
      />
    );
  }

  function renderSecondaryButton() {
    if (!secondaryButton) return null;

    const theme = {
      primaryColor: noticeStyle,
      primaryColorDark: `${noticeStyle}Dark`,
      primaryColorLight: `${noticeStyle}Light`,
      primaryColorTint: ["warning", "neutral"].includes(noticeStyle) ? "black" : "white",
    };

    return (
      <Button
        mr={button ? "m" : "none"}
        buttonStyle="tertiary"
        themeOverrides={theme}
        {...secondaryButton}
        justifySelf="flex-end"
        size="small"
      />
    );
  }

  return (
    <Flex
      data-testid={id}
      alignItems="center"
      p="s"
      mt="xs"
      borderRadius="default"
      textAlign="left"
      {...style}
      {...rest}>
      {renderIcon()}
      <Text
        flex="1"
        data-testid="notice-message"
        font="ui.small"
        mr={button || secondaryButton ? "m" : "none"}>
        {message}
      </Text>
      {renderSecondaryButton()}
      {renderButton()}
    </Flex>
  );
};

export default Notice;
